import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import React, { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { verifoneService } from '~/services/Verifone';
import {
  detectCardBrand,
  formatCardNumber,
  formatExpiryDate,
} from '~/utils/cardValidators';

import styles from './VerifoneCardForm.module.scss';

interface VerifoneCardFormProps {}

export const VerifoneCardForm: React.FC<VerifoneCardFormProps> = observer(
  () => {
    const { t } = useTranslation();
    const [cardNumber, setCardNumber] = useState('');
    const [expiryDate, setExpiryDate] = useState('');
    const [cvv, setCvv] = useState('');

    const handleCardNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(/\s/g, '');
      if (/^\d*$/.test(value) && value.length <= 19) {
        setCardNumber(value);
        const brand = detectCardBrand(value);
        verifoneService.setCardBrand(brand || '');
        verifoneService.clearValidationError();
      }
    };

    const handleExpiryDateChange = (e: ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value;

      if (value.endsWith('/') && value.length === 3) {
        value = value.slice(0, -2);
      }

      const formatted = formatExpiryDate(value);

      if (formatted.length <= 5) {
        setExpiryDate(formatted);
        verifoneService.clearValidationError();
      }
    };

    const handleCVVChange = (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (/^\d*$/.test(value) && value.length <= 4) {
        setCvv(value);
        verifoneService.clearValidationError();
      }
    };

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
    };

    return (
      <form className={styles.cardForm} onSubmit={handleSubmit}>
        <div className={styles.cardNumber}>
          <div className={styles.inputGroup}>
            <div
              className={classes([
                styles.inputWrapper,
                verifoneService.validationError &&
                  verifoneService.validationError.includes('card number') &&
                  styles.error,
              ])}
            >
              <input
                data-cardinal-field="AccountNumber"
                id="cardNumber"
                type="text"
                className={styles.input}
                value={formatCardNumber(cardNumber)}
                onChange={handleCardNumberChange}
                placeholder=""
                autoComplete="cc-number"
              />
            </div>
          </div>
        </div>

        <div className={styles.expiryRow}>
          <div className={styles.inputGroup}>
            <label htmlFor="expiryDate" className={styles.label}>
              {t('cardDetails:expiryDate')}
            </label>
            <div
              className={classes([
                styles.inputWrapper,
                verifoneService.validationError &&
                  verifoneService.validationError.includes('expiry') &&
                  styles.error,
              ])}
            >
              <input
                id="expiryDate"
                type="text"
                className={styles.input}
                value={expiryDate}
                onChange={handleExpiryDateChange}
                placeholder="MM/YY"
                autoComplete="cc-exp"
                maxLength={5}
              />
            </div>
            <div className={styles.hint}>{t('cardDetails:expiryDateTip')}</div>
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="cvv" className={styles.label}>
              {t('cardDetails:securityCode')}
            </label>
            <div
              className={classes([
                styles.inputWrapper,
                verifoneService.validationError &&
                  verifoneService.validationError.includes('CVV') &&
                  styles.error,
              ])}
            >
              <input
                id="cvv"
                type="text"
                className={styles.input}
                value={cvv}
                onChange={handleCVVChange}
                placeholder=""
                autoComplete="cc-csc"
              />
            </div>
            <div className={styles.hint}>
              {t('cardDetails:securityCodeTip')}
            </div>
          </div>
        </div>

        {verifoneService.validationError && (
          <div className={styles.cardAlert}>
            {verifoneService.validationError}
          </div>
        )}
      </form>
    );
  },
);

export default VerifoneCardForm;
